import { API_ENDPOINTS, getApiUrl, getTransactionalApiUrl } from "../config";
import { useSessionStore } from "../../../state/src/useSessionStore";
import { buildBffRequestHeaders } from "./bffRequestHeaders";

export interface MapiProfileSearchRequest {
  agentPartnerId: string;
  mobilePhone: string;
  userLanguage?: string;
}

export interface MapiProfileSearchResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

export const mapiProfileSearch = async (
  params: MapiProfileSearchRequest,
): Promise<MapiProfileSearchResponse> => {
  const url = getTransactionalApiUrl(API_ENDPOINTS.MAPI_PROFILE_SEARCH);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `MAPI profile search failed: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    return data as MapiProfileSearchResponse;
  } catch (error) {
    console.error("MAPI profile search error:", error);
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error("CORS Error: Unable to connect to the API server.");
    }
    throw error;
  }
};

export interface MapiQuoteRequest {
  agentPartnerId: string;
  destinationCountryCode: string;
  destinationCountrySubdivisionCode?: string;
  serviceOptionCode?: string;
  sendAmount: {
    value: number;
    currencyCode?: string;
  };
  receiveCurrencyCode?: string;
  fundInStore?: { fundInStore?: boolean };
  promotionCodes?: string[];
}

export interface MapiQuoteResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

function formatMapiErrorsForDisplay(errors: any[] | undefined): string {
  if (!Array.isArray(errors) || errors.length === 0) return "";
  return errors
    .map((e: any) => {
      let line = e?.message || "";
      if (Array.isArray(e?.offendingFields) && e.offendingFields.length > 0) {
        const names = e.offendingFields
          .map((f: any) =>
            f && typeof f === "object" && f.field != null
              ? String(f.field)
              : String(f),
          )
          .join(", ");
        line = line ? `${line} (fields: ${names})` : `Invalid fields: ${names}`;
      }
      return line || "Unknown error";
    })
    .filter(Boolean)
    .join("; ");
}

function buildMapiHttpErrorMessage(
  data: any,
  text: string,
  statusFallback: string,
): string {
  const fromErrors =
    formatMapiErrorsForDisplay(data?.errors) ||
    formatMapiErrorsForDisplay(data?.data?.errors);
  return (
    (typeof data?.formattedMessage === "string" &&
      data.formattedMessage.trim()) ||
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof data?.error === "string" && data.error.trim()) ||
    fromErrors ||
    text ||
    statusFallback
  );
}

export const mapiQuote = async (
  params: MapiQuoteRequest,
): Promise<MapiQuoteResponse> => {
  const url = getTransactionalApiUrl(API_ENDPOINTS.MAPI_QUOTE);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(params),
    });

    const text = await response.text();
    let data: any;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      if (!response.ok) {
        throw new Error(text || `Quote failed (${response.status})`);
      }
      throw new Error("Invalid response from quote service.");
    }

    if (!response.ok) {
      throw new Error(
        buildMapiHttpErrorMessage(
          data,
          text,
          `Quote failed (${response.status})`,
        ),
      );
    }

    return data as MapiQuoteResponse;
  } catch (error) {
    console.error("MAPI quote error:", error);
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(
        "Unable to connect to the quote service. Check your network or server.",
      );
    }
    throw error;
  }
};

export interface MapiTransactionFieldsSendRequest {
  agentPartnerId: string;
  destinationCountryCode: string;
  serviceOptionCode: string;
  serviceOptionRoutingCode: string;
  amount: number;
  sendCurrencyCode: string;
  receiveCurrencyCode: string;
  userLanguage?: string;
}

export interface MapiTransactionFieldsSendResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

export const mapiTransactionFieldsSend = async (
  params: MapiTransactionFieldsSendRequest,
): Promise<MapiTransactionFieldsSendResponse> => {
  const url = getTransactionalApiUrl(
    API_ENDPOINTS.MAPI_TRANSACTION_FIELDS_SEND,
  );

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(params),
    });

    const text = await response.text();
    let data: any;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      if (!response.ok) {
        throw new Error(
          text || `Transaction fields request failed (${response.status})`,
        );
      }
      throw new Error("Invalid response from transaction fields service.");
    }

    if (!response.ok) {
      throw new Error(
        buildMapiHttpErrorMessage(
          data,
          text,
          `Transaction fields request failed (${response.status})`,
        ),
      );
    }

    return data as MapiTransactionFieldsSendResponse;
  } catch (error) {
    console.error("MAPI transaction fields send error:", error);
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(
        "Unable to connect to the transaction fields service. Check your network or server.",
      );
    }
    throw error;
  }
};

export interface MapiCountriesResponse {
  success: boolean;
  data: any[];
  message: string;
  error?: string;
}

export const mapiLoadCountries = async (): Promise<MapiCountriesResponse> => {
  const url = getTransactionalApiUrl(API_ENDPOINTS.MAPI_COUNTRIES);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: buildBffRequestHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: [],
        message:
          data?.message || `Failed to load countries (${response.status})`,
        error: data?.error || data?.message,
      };
    }

    return data as MapiCountriesResponse;
  } catch (error) {
    console.error("MAPI load countries error:", error);
    return {
      success: false,
      data: [],
      message: (error as Error)?.message || "Failed to load countries.",
      error: (error as Error)?.message,
    };
  }
};

export interface MapiCountryDetailsResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

export const mapiGetCountryDetails = async (
  iso3Code: string,
): Promise<MapiCountryDetailsResponse> => {
  const url = getTransactionalApiUrl(
    `${API_ENDPOINTS.MAPI_COUNTRY_DETAILS}/${iso3Code}`,
  );

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: buildBffRequestHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: {},
        message:
          data?.message ||
          `Failed to load country details (${response.status})`,
        error: data?.error || data?.message,
      };
    }

    return data as MapiCountryDetailsResponse;
  } catch (error) {
    console.error("MAPI country details error:", error);
    return {
      success: false,
      data: {},
      message: (error as Error)?.message || "Failed to load country details.",
      error: (error as Error)?.message,
    };
  }
};

export interface MapiCreateProfileRequest {
  profileData: any;
}

export interface MapiCreateProfileResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

export const mapiCreateProfile = async (
  params: MapiCreateProfileRequest,
): Promise<MapiCreateProfileResponse> => {
  const url = getTransactionalApiUrl(API_ENDPOINTS.MAPI_CREATE_PROFILE);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: data?.data || data,
        message:
          data?.message ||
          data?.error ||
          `Create profile failed (${response.status})`,
        error: data?.error || data?.message,
      };
    }

    return data as MapiCreateProfileResponse;
  } catch (error) {
    console.error("MAPI create profile error:", error);
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      return {
        success: false,
        data: {},
        message: "CORS Error: Unable to connect to the API server.",
        error: "CORS Error",
      };
    }
    return {
      success: false,
      data: {},
      message: (error as Error)?.message || "An unknown error occurred.",
      error: (error as Error)?.message,
    };
  }
};

export interface MapiUpdateTransactionRequest {
  transactionId: string;
  updateData: any;
}

export interface MapiUpdateTransactionResponse {
  success: boolean;
  data: any;
  message: string;
  error?: string;
}

export const mapiUpdateTransaction = async (
  params: MapiUpdateTransactionRequest,
): Promise<MapiUpdateTransactionResponse> => {
  const url = getTransactionalApiUrl(API_ENDPOINTS.MAPI_UPDATE_TRANSACTION);

  try {
    const response = await fetch(url, {
      method: "PUT",
      headers: buildBffRequestHeaders(),
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        data: data?.data || data,
        message:
          data?.message ||
          data?.error ||
          `Update transaction failed (${response.status})`,
        error: data?.error || data?.message,
      };
    }

    return data as MapiUpdateTransactionResponse;
  } catch (error) {
    console.error("MAPI update transaction error:", error);
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      return {
        success: false,
        data: {},
        message: "CORS Error: Unable to connect to the API server.",
        error: "CORS Error",
      };
    }
    return {
      success: false,
      data: {},
      message: (error as Error)?.message || "An unknown error occurred.",
      error: (error as Error)?.message,
    };
  }
};
